import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Spin, message } from 'antd';
import { StateObjectCell } from 'molstar/lib/mol-state/object';

interface MolStarChainViewerProps {
  pdbData: string;
  height?: number;
  width?: string;
  ligandChains?: string[];
  receptorChains?: string[];
  onChainClick?: (chainId: string) => void;
  onResidueClick?: (chainId: string, residueNumber: number, residueName: string) => void;
}

const MolStarChainViewer: React.FC<MolStarChainViewerProps> = ({
  pdbData,
  height = 500,
  width = '100%',
  ligandChains = [],
  receptorChains = [],
  onChainClick,
  onResidueClick
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pluginRef = useRef<any>(null);
  const structureRef = useRef<any>(null);
  const presentChainIdsRef = useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoverInfo, setHoverInfo] = useState<string>('');
  const isUnmountingRef = useRef(false);
  const initializedRef = useRef(false);
  const highlightLockRef = useRef<Promise<void>>(Promise.resolve());

  // 根据选中的配体/受体链在3D视图中高亮对应链（配体红色、受体蓝色）
  const updateChainHighlights = useCallback(async () => {
    const plugin = pluginRef.current;
    const structure = structureRef.current;
    if (!plugin || !structure) return;

    try {
      // 移除旧的高亮组件（组件删除时其下的表示会一并移除）
      const state = plugin.state.data;
      const highlightCells = (Array.from(state.cells.values()) as any[]).filter((c: any) =>
        c.transform?.tags?.some((t: string) => t.startsWith('chain-highlight-') && !t.endsWith('-rep'))
      );
      for (const cell of highlightCells) {
        try { await state.remove(cell.transform.ref); } catch { /* 已随父节点移除 */ }
      }

      if (ligandChains.length === 0 && receptorChains.length === 0) return;

      const { MolScriptBuilder } = await import('molstar/lib/mol-script/language/builder');
      const { Color } = await import('molstar/lib/mol-util/color');
      const MS = MolScriptBuilder;

      const addChainHighlight = async (chainId: string, colorValue: number, tag: string) => {
        // 仅高亮结构中实际存在的链
        if (!presentChainIdsRef.current.has(chainId)) return;
        const expr = MS.struct.generator.atomGroups({
          'chain-test': MS.core.rel.eq([MS.struct.atomProperty.macromolecular.auth_asym_id(), chainId])
        });
        const comp = await plugin.builders.structure.tryCreateComponentFromExpression(
          structure, expr, tag, { label: `链 ${chainId}`, tags: [tag] }
        );
        if (!comp || !comp.cell) return;
        await plugin.builders.structure.representation.addRepresentation(comp, {
          type: 'cartoon',
          color: 'uniform',
          colorParams: { value: Color(colorValue) },
          size: 'uniform'
        }, { tag: `${tag}-rep` });
      };

      for (const chainId of ligandChains) {
        await addChainHighlight(chainId, 0xe74c3c, `chain-highlight-ligand-${chainId}`);
      }
      for (const chainId of receptorChains) {
        await addChainHighlight(chainId, 0x3498db, `chain-highlight-receptor-${chainId}`);
      }
    } catch (e) {
      console.warn('更新链高亮失败:', e);
    }
  }, [ligandChains, receptorChains]);

  // 用ref持有最新高亮函数，避免initMolStar因依赖变化而重新初始化
  const updateChainHighlightsRef = useRef(updateChainHighlights);
  updateChainHighlightsRef.current = updateChainHighlights;

  // 初始化MolStar viewer
  const initMolStar = useCallback(async () => {
    if (!containerRef.current || !pdbData || initializedRef.current) return;

    try {
      isUnmountingRef.current = false;
      setLoading(true);
      setError(null);

      // 动态导入Mol*插件
      const { PluginContext } = await import('molstar/lib/mol-plugin/context');
      const { DefaultPluginSpec } = await import('molstar/lib/mol-plugin/spec');
      const { PluginConfig } = await import('molstar/lib/mol-plugin/config');

      // 创建简化的插件配置，移除可能导致问题的插件
      const spec = DefaultPluginSpec();
      
      // 移除可能导致 "No suitable parent found" 错误的插件
      spec.behaviors = spec.behaviors.filter(b => 
        !b.transformer.id.includes('computed-valence-model-prop') &&
        !b.transformer.id.includes('integrative-cross-link-restraint') &&
        !b.transformer.id.includes('representation-highlight-loci') &&
        !b.transformer.id.includes('representation-select-loci') &&
        !b.transformer.id.includes('default-loci-label-provider') &&
        !b.transformer.id.includes('representation-focus-loci') &&
        !b.transformer.id.includes('camera-focus-loci') &&
        !b.transformer.id.includes('camera-axis-helper') &&
        !b.transformer.id.includes('create-structure-focus-representation') &&
        !b.transformer.id.includes('structure-info-prop') &&
        !b.transformer.id.includes('computed-accessible-surface-area-prop') &&
        !b.transformer.id.includes('sifts-mapping-prop') &&
        !b.transformer.id.includes('computed-interactions-prop') &&
        !b.transformer.id.includes('computed-secondary-structure-prop')
      );
      
      spec.config = [
        [PluginConfig.General.DisableAntialiasing, false],
        [PluginConfig.General.PixelScale, 1],
        [PluginConfig.Viewport.ShowAnimation, false],
        [PluginConfig.Viewport.ShowControls, false]
      ];

      // 创建插件实例
      const molstarPlugin = new PluginContext(spec);
      molstarPlugin.mount(containerRef.current!);
      await molstarPlugin.init();

      // 检测数据格式（PDB 或 CIF）
      const isCif = pdbData.trim().startsWith('data_') || pdbData.includes('_atom_site.');
      const format = isCif ? 'mmcif' : 'pdb';
      
      console.log(`检测到结构格式: ${format}`);
      
      // 加载结构数据
      const data = await molstarPlugin.builders.data.rawData({ 
        data: pdbData, 
        label: 'Protein Structure' 
      });
      
      // 解析 trajectory
      const trajectory = await molstarPlugin.builders.structure.parseTrajectory(data, format);
      
      // 创建 model - 使用 trajectory 的第一个模型
      const model = await molstarPlugin.builders.structure.createModel(trajectory, { modelIndex: 0 });
      
      // 如果 model 创建失败，尝试备用方法
      if (!model || !model.cell || !model.cell.obj) {
        console.warn('使用标准方法创建模型失败，尝试备用方法');
        throw new Error('无法创建结构模型');
      }
      
      // 显式使用完整模型（asymmetric unit）。
      // molstar 的 createStructure 不传参时默认加载第 1 个生物组装（assembly），
      // 而 PDB 的 REMARK 350 BIOMOLECULE 1 可能只包含部分链（如 6YJP 只含 C+A），
      // 会导致视口缺链、无法选链。
      const structure = await molstarPlugin.builders.structure.createStructure(model, { name: 'model', params: {} });

      // 添加基础卡通表示
      // 采用按链分组的多实例表示，使每条链天然分离，便于叠加高亮
      await molstarPlugin.builders.structure.representation.addRepresentation(structure, {
        type: 'cartoon',
        color: 'chain-id',
        size: 'uniform'
      }, { tag: 'base-cartoon' });

      // 统计结构中存在的链ID（label/auth 两种）
      try {
        const chains = model.cell?.obj?.data?.atomicHierarchy?.chains;
        if (chains) {
          const labelArr = chains.label_asym_id?.toArray?.() || [] as any;
          const authArr = chains.auth_asym_id?.toArray?.() || [] as any;
          const ids = new Set<string>();
          // 使用索引遍历以兼容 ArrayLike
          for (let i = 0; i < (labelArr as any).length; i++) {
            const id = (labelArr as any)[i];
            if (id) ids.add(String(id));
          }
          for (let i = 0; i < (authArr as any).length; i++) {
            const id = (authArr as any)[i];
            if (id) ids.add(String(id));
          }
          presentChainIdsRef.current = ids;
          console.log('[MolStar] 结构中链ID:', Array.from(ids));
        }
      } catch (e) {
        console.warn('统计链ID失败:', e);
      }

      // 设置悬浮显示链和残基信息
      molstarPlugin.behaviors.interaction.hover.subscribe(async (event: any) => {
        try {
          const loci = event?.current?.loci;
          // 增加更严格的检查：确保 loci 存在且有 kind 属性
          if (!loci || typeof loci !== 'object' || !loci.kind || loci.kind !== 'element-loci' || !loci.elements || loci.elements.length === 0) {
            setHoverInfo('');
            return;
          }

          const { StructureElement, StructureProperties } = await import('molstar/lib/mol-model/structure');
          const first = StructureElement.Loci.getFirstLocation(loci as any);
          if (!first) {
            setHoverInfo('');
            return;
          }

          // 获取链ID（优先 auth，其次 label）
          const authId = StructureProperties.chain.auth_asym_id(first);
          const labelId = StructureProperties.chain.label_asym_id(first);
          const chainId = authId || labelId;

          // 获取残基名称与序号（优先 auth_seq，其次 label_seq）
          // 获取残基名称（使用原子 label_comp_id 更兼容）
          const resName = StructureProperties.atom.label_comp_id(first);
          const authSeq = StructureProperties.residue.auth_seq_id(first);
          const labelSeq = StructureProperties.residue.label_seq_id(first);
          const resNo = authSeq || labelSeq;

          const info = chainId && resName ? `链: ${chainId} | 氨基酸: ${resName}${resNo ? resNo : ''}` : '';
          setHoverInfo(info);
        } catch (e) {
          setHoverInfo('');
        }
      });

      // 聚焦到结构
      try {
        const structureData = molstarPlugin.state.data.select(structure.ref)[0];
        if (structureData?.obj?.data) {
          const { StructureElement } = await import('molstar/lib/mol-model/structure');
          const wholeLoci = StructureElement.Loci.all(structureData.obj.data);
          await molstarPlugin.managers.camera.focusLoci([wholeLoci]);
        } else {
          await molstarPlugin.managers.camera.reset();
        }
      } catch (focusError) {
        console.warn('聚焦结构失败，使用默认视角:', focusError);
        await molstarPlugin.managers.camera.reset();
      }

      pluginRef.current = molstarPlugin;
      structureRef.current = structure;
      initializedRef.current = true;
      setLoading(false);

      // 布局稳定后强制同步一次画布尺寸，避免初始尺寸错误导致结构显示不全
      requestAnimationFrame(() => {
        if (isUnmountingRef.current) return;
        try { (molstarPlugin as any).canvas3d?.requestResize?.(); } catch { /* 忽略 */ }
      });

      // 应用当前已选中的链高亮（用户可能在加载完成前已勾选）
      updateChainHighlightsRef.current();

      console.log('MolStar chain viewer初始化成功');
    } catch (error) {
      console.error('MolStar初始化失败:', error);
      setError('3D结构加载失败');
      setLoading(false);
      message.error('3D结构加载失败');
    }
  }, [pdbData]);



  // 初始化viewer
  useEffect(() => {
    initMolStar();
    
    return () => {
      isUnmountingRef.current = true;
      if (pluginRef.current) {
        try {
          pluginRef.current.dispose();
        } catch (error) {
          console.error('清理MolStar插件失败:', error);
        }
      }
      initializedRef.current = false;
    };
  }, [initMolStar]);

  // 监听容器尺寸变化，同步 MolStar 画布（防止容器尺寸变化后结构被裁切、显示不全）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      try { (pluginRef.current as any)?.canvas3d?.requestResize?.(); } catch { /* 忽略 */ }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // 选中链变化时更新高亮（串行执行，避免并发重建冲突）
  useEffect(() => {
    if (!initializedRef.current) return;
    highlightLockRef.current = highlightLockRef.current
      .then(() => updateChainHighlights())
      .catch(() => {});
  }, [updateChainHighlights]);

  if (error) {
    return (
      <div 
        style={{ 
          width, 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          border: '1px solid #f0f0f0',
          borderRadius: '4px',
          color: '#999'
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width, height }}>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          border: '1px solid #f0f0f0',
          borderRadius: '4px'
        }}
      />
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000
          }}
        >
          <Spin size="large" tip="加载3D结构中..." />
        </div>
      )}
      {hoverInfo && (
        <div
          style={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            padding: '8px 12px',
            background: 'rgba(0, 0, 0, 0.75)',
            color: '#fff',
            borderRadius: '4px',
            fontSize: '13px',
            fontFamily: 'monospace',
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        >
          {hoverInfo}
        </div>
      )}
    </div>
  );
};

export default MolStarChainViewer;


